import { useMemo } from 'react';
import { extractSections } from './markdown';
import LegalSectionV2 from './LegalSectionV2';
import type { LegalDocMeta } from './types';

type Props = {
  meta: LegalDocMeta;
  subtitle: string;
  lastUpdated: string;
  content: string;
};

export default function LegalDocV2({ meta, subtitle, lastUpdated, content }: Props) {
  const sections = useMemo(() => extractSections(content), [content]);

  return (
    <section className="bg-paper py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid gap-12 lg:grid-cols-[260px_1fr] lg:gap-14">
          {/* TOC sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-5">
              <div className="mb-4 flex items-center gap-2.5 font-marketing-mono text-[10px] font-medium uppercase tracking-[0.1em] text-charcoal-500">
                <span className="h-px w-5 bg-lime-500" />
                Sommaire
              </div>
              <ol className="flex flex-col gap-1.5">
                {sections.map((section) => (
                  <li key={section.id} className="flex gap-2.5">
                    <span className="pt-[3px] font-marketing-mono text-[10px] font-medium tracking-[0.05em] text-charcoal-400">
                      {section.num}
                    </span>
                    <a
                      href={`#${section.id}`}
                      className="font-marketing-body text-[13px] leading-[1.45] text-charcoal-600 transition-colors hover:text-lime-600"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-5 border-t border-charcoal-100 pt-4 font-marketing-mono text-[10px] uppercase tracking-[0.08em] text-charcoal-400">
                MAJ · <strong className="font-bold text-charcoal-800">{lastUpdated}</strong>
              </div>
            </div>
          </aside>

          {/* Doc body */}
          <div className="max-w-[760px]">
            <header className="mb-12 border-[3px] border-charcoal-800 bg-charcoal-800 p-8 text-white shadow-neobrutal-lime-10 md:p-9">
              <span className="mb-4 inline-block border-[1.5px] border-lime-500 bg-lime-500 px-2.5 py-[3px] font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-charcoal-800">
                {meta.tag}
              </span>
              <h2 className="font-marketing-display text-[32px] font-bold leading-[1.05] tracking-[-0.03em] md:text-[38px]">
                {meta.title}
              </h2>
              <p className="mt-3 max-w-[52ch] font-marketing-body text-[15px] leading-[1.6] text-charcoal-200">
                {subtitle}
              </p>
              <div className="mt-5 flex flex-wrap gap-5 border-t border-charcoal-700 pt-4 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-400">
                <span>
                  En vigueur depuis · <b className="font-semibold text-lime-500">{lastUpdated}</b>
                </span>
                <span>
                  Temps de lecture · <b className="font-semibold text-lime-500">{meta.readTime}</b>
                </span>
              </div>
            </header>

            <div>
              {sections.map((section) => (
                <LegalSectionV2 key={section.id} section={section} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
